import * as React from 'react';
// mui
import { Box, Card, CardHeader, Grid, Skeleton, Stack, Tooltip } from '@mui/material';
// dayjs
import dayjs from 'dayjs';
// components
import api from '../../axios';

interface HeatmapCompletionObject {
  date: string;
  opacity: number;
}

interface APICompletionObject {
  date: string;
  completions: number;
}

export default function CompletionsHeatmap() {
  const [ heatmapValues, setHeatmapValues ] = React.useState<HeatmapCompletionObject[][]>([]);
  const [ monthLabels, setMonthLabels ] = React.useState<(string | undefined)[]>([]);
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'July', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];

  React.useEffect(() => {
    const startDate = dayjs().startOf('day').subtract(1, 'year').startOf('week');
    const endDate = dayjs().startOf('day');

    api.post('/dashboard/completions', {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString()
    })
      .then((res) => {
        console.log(res);
        const completionData = res.data;

        let highestCompletionsInDay = completionData.reduce((prevHighest: number, day: APICompletionObject) => {
          return Math.max(prevHighest, day.completions);
        }, 0);

        let tempMonthValues: (string | undefined)[] = Array.from({ length: 54 });
        let tempValues: HeatmapCompletionObject[][] = [];

        let prevMonth = -1;
        for (let i = 0; i < completionData.length; i += 1) {
          const month = dayjs(completionData[i].date).month(); // 0 - 11
          const week = Math.floor(i / 7);
          const day = i % 7;
          if (day === 0) {
            tempValues.push([]);
          }
          if (month !== prevMonth) {
            tempMonthValues[week+2] = months[month];
            prevMonth = month;
          }
          tempValues[week][day] = {
            date: completionData[i].date,
            opacity: ((completionData[i].completions / highestCompletionsInDay) * 0.95) + 0.05
          }
        }

        setHeatmapValues(tempValues);
        setMonthLabels(tempMonthValues);
      })
  }, [])

  return (
    <Card sx={{ p: 2, pt: 1, pb: 4 }} variant="outlined">
      <CardHeader subheader="Tasks Completed" />
      <Box sx={{ width: '100%', pl: 2, pr: 1 }}>
        <Grid container columns={55} spacing={0.5}>
          {(monthLabels.length > 0) ? (
            <React.Fragment>
              {monthLabels.map((label, i) => (
                <Grid key={i} size={1}>
                  <Box
                    sx={{
                      minWidth: '100%',
                      width: '100%',
                      height: '20px',
                      p: 0,
                      m: 0,
                      fontSize: '0.75rem',
                    }}
                  >{label}</Box>
                </Grid>
              ))}
            </React.Fragment>
          ): (
            <React.Fragment>
              {Array.from({ length: 52 }).map((label, i) => (
                <Grid key={i} size={1}>
                  <Box
                    sx={{
                      minWidth: '100%',
                      width: '100%',
                      height: '20px',
                      p: 0,
                      m: 0,
                      fontSize: '0.75rem',
                    }}
                  > </Box>
                </Grid>
              ))}
            </React.Fragment>
          )}
        </Grid>
        <Grid container columns={55} spacing={0.5}>
          <Grid size={1}>
            <Stack spacing={0.5}>
              <React.Fragment>
                {days.map((day, i) => (
                  <Box 
                    key={i}
                    sx={{
                      minWidth: '100%',
                      width: '100%',
                      height: '0 !important',
                      p: 0,
                      pb: '100%',
                      m: 0,
                      textAlign: 'right',
                      direction: 'rtl',
                      fontSize: '0.75rem',
                    }}
                  >{day}</Box>
                ))}
              </React.Fragment>
            </Stack>
          </Grid>
          {(heatmapValues.length > 0) ? (
            <React.Fragment>
              {heatmapValues.map((valueArr, i) => (
                <Grid key={i} size={1}>
                  <Stack spacing={0.5}>
                    {valueArr.map((day: HeatmapCompletionObject, n) => (
                      <Box key={n} sx={{ m: 0, p: 0 }}>
                        <Tooltip
                          title={day.date}
                        >
                          <Box sx={{
                            backgroundColor: `rgb(85,108,214,${day.opacity})`,
                            minWidth: '100%',
                            width: '100%',
                            height: '0',
                            p: 0,
                            pb: '100%',
                            m: 0
                          }}></Box>
                        </Tooltip>
                      </Box>
                    ))}
                  </Stack>
                </Grid>
              ))}
            </React.Fragment>
          ) : (
            <React.Fragment>
              {Array.from({ length: 52 }).map((_, i) => (
                <Grid key={i} size={1}>
                  <Stack spacing={0.5}>
                    {Array.from({ length: 7 }).map((_, n) => (
                      <Skeleton
                        key={n}
                        variant='rectangular'
                        sx={{
                          minWidth: '100%',
                          width: '100%',
                          height: '0',
                          p: 0,
                          pb: '100%',
                          m: 0
                        }} 
                      ></Skeleton>
                    ))}
                  </Stack>
                </Grid>
              ))}
            </React.Fragment>
          )}
        </Grid>
      </Box>
    </Card>
  );
}